import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CircleDollarSign, Layers3, ListOrdered, Trophy } from "lucide-react";

import { api } from "@/api/client";
import type { CostQueryResult } from "@/api/types";
import { MetricTile } from "@/components/MetricTile";
import { Section } from "@/components/Section";

const RANGES = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
];

type GroupBy = "service" | "pool" | "cloud" | "replica";

const GROUP_LABEL: Record<GroupBy, string> = {
  service: "Service",
  pool: "Pool",
  cloud: "Cloud",
  replica: "Replica",
};

export function GlobalCost() {
  const [groupBy, setGroupBy] = useState<GroupBy>("service");
  const [days, setDays] = useState(7);

  const range = useQuery({
    queryKey: ["cost", groupBy, days],
    queryFn: () => {
      const to = new Date().toISOString();
      const from = new Date(Date.now() - days * 86_400_000).toISOString();
      return api.get<CostQueryResult>(
        `/cost?group_by=${groupBy}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      );
    },
  });

  const sorted = useMemo(
    () =>
      [...(range.data?.buckets ?? [])].sort((a, b) => b.cost_usd - a.cost_usd),
    [range.data],
  );
  const top10 = sorted.slice(0, 10);
  const totalSeconds = useMemo(
    () => sorted.reduce((acc, b) => acc + b.replica_seconds, 0),
    [sorted],
  );
  // Hours the ledger knows happened but couldn't price. Shown rather
  // than absorbed: an incomplete total the operator knows is
  // incomplete is useful, while one that silently treats unpriced
  // hours as free is just wrong.
  const unpricedHours = (range.data?.unpriced_seconds ?? 0) / 3600;
  const avgPerHour =
    totalSeconds > 0
      ? (((range.data?.total_usd ?? 0) / totalSeconds) * 3600).toFixed(4)
      : "—";

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as GroupBy)}
        >
          <option value="service">by service</option>
          <option value="pool">by pool</option>
          <option value="cloud">by cloud</option>
          <option value="replica">by replica</option>
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {RANGES.map((r) => (
            <option key={r.days} value={r.days}>
              last {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="metric-grid">
        <MetricTile
          icon={<CircleDollarSign />}
          label={`Total · ${days}d`}
          value={`$${(range.data?.total_usd ?? 0).toFixed(2)}`}
          tone="brand"
          hint={
            unpricedHours > 0
              ? `${sorted.length} ${groupBy} bucket${sorted.length === 1 ? "" : "s"} · floor: +${unpricedHours.toFixed(1)}h unpriced`
              : `${sorted.length} ${groupBy} bucket${sorted.length === 1 ? "" : "s"}`
          }
        />
        <MetricTile
          icon={<Layers3 />}
          label="Buckets"
          value={sorted.length}
          unit={groupBy}
        />
        <MetricTile
          icon={<Trophy />}
          label="Top spender"
          value={top10[0]?.group_key ?? "—"}
          hint={
            top10[0]
              ? `$${top10[0].cost_usd.toFixed(2)} (${(
                  (top10[0].cost_usd / (range.data?.total_usd || 1)) *
                  100
                ).toFixed(1)}%)`
              : "no spend in window"
          }
          tone={top10[0] ? "warn" : "neutral"}
        />
        <MetricTile
          icon={<ListOrdered />}
          label="Avg $/replica-hour"
          value={avgPerHour}
          unit="USD"
          hint="weighted across the window"
        />
      </div>

      <Section
        title="Top 10"
        icon={<Trophy />}
        count={`${groupBy} · ${top10.length}`}
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={top10}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--edge)" />
            <XAxis
              dataKey="group_key"
              stroke="var(--ink-muted)"
              tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
            />
            <YAxis
              stroke="var(--ink-muted)"
              tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
            />
            <Tooltip
              contentStyle={{
                background: "var(--surface)",
                border: "1px solid var(--edge-strong)",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: "var(--ink-muted)" }}
              cursor={{ fill: "var(--brand-soft)" }}
            />
            <Bar
              dataKey="cost_usd"
              name="USD"
              fill="var(--brand)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Section>

      <Section
        title="All buckets"
        icon={<ListOrdered />}
        count={`${sorted.length}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{GROUP_LABEL[groupBy]}</th>
                <th style={{ textAlign: "right" }}>USD</th>
                <th style={{ textAlign: "right" }}>Replica seconds</th>
                <th style={{ textAlign: "right" }}>$/replica-h (avg)</th>
                <th style={{ textAlign: "right" }}>% of total</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((b) => (
                <tr key={b.group_key} className="hoverable">
                  <td>{b.group_key}</td>
                  <td className="num">{b.cost_usd.toFixed(4)}</td>
                  <td className="num muted">{b.replica_seconds.toFixed(0)}</td>
                  <td className="num muted">
                    {b.replica_seconds > 0
                      ? ((b.cost_usd / b.replica_seconds) * 3600).toFixed(4)
                      : "—"}
                  </td>
                  <td className="num muted">
                    {range.data?.total_usd
                      ? ((b.cost_usd / range.data.total_usd) * 100).toFixed(1) +
                        "%"
                      : "—"}
                  </td>
                </tr>
              ))}
              {!sorted.length && (
                <tr>
                  <td colSpan={5} className="empty">
                    no spend recorded in the last {days} day
                    {days === 1 ? "" : "s"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}
